/** Static per-page data for the batch config grid's detail panels.
 *
 * The DataGrid's `getDetailPanelContent` callback must stay referentially
 * stable (a new callback remounts every open panel), so panels cannot receive
 * page data through props — they pull it from this context instead. Pending
 * edits are NOT here; they live in `ChangeSetContext`.
 */
import { createContext, useContext, type ReactNode } from 'react';

import type { TenantConfigItem, TenantConfigList } from '@/api/tourOperatorConfigs';

import type { ConfigIndex } from './configIndex';

export interface BatchData {
  index: ConfigIndex;
  tenantById: Map<number, TenantConfigItem>;
  tenantCount: number;
  refetchConfigs: () => Promise<TenantConfigList | undefined>;
}

const Ctx = createContext<BatchData | null>(null);

export function BatchDataProvider({ value, children }: { value: BatchData; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useBatchData(): BatchData {
  const v = useContext(Ctx);
  if (!v) throw new Error('useBatchData must be used inside <BatchDataProvider>');
  return v;
}
